import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

import type { AdminOrdersDeadlineBucket } from "@/features/marketplace/types/marketplace.types";

interface AdminOrdersDeadlineWidgetProps {
  counts: Record<AdminOrdersDeadlineBucket, number>;
  activeBucket?: AdminOrdersDeadlineBucket;
  onSelect: (bucket: AdminOrdersDeadlineBucket) => void;
}

const DEADLINE_BUCKETS = [
  {
    bucket: "overdue",
    labelKey: "marketplace.ordersDashboard.overview.deadlines.overdue",
    label: "Overdue",
    color: "var(--error)",
  },
  {
    bucket: "today",
    labelKey: "marketplace.ordersDashboard.overview.deadlines.today",
    label: "Due today",
    color: "var(--warning)",
  },
  {
    bucket: "next7Days",
    labelKey: "marketplace.ordersDashboard.overview.deadlines.next7Days",
    label: "Next 7 days",
    color: "var(--chart-yellow)",
  },
  {
    bucket: "later",
    labelKey: "marketplace.ordersDashboard.overview.deadlines.later",
    label: "Later",
    color: "var(--chart-blue)",
  },
  {
    bucket: "unscheduled",
    labelKey: "marketplace.ordersDashboard.overview.deadlines.unscheduled",
    label: "Unscheduled",
    color: "var(--muted-foreground)",
  },
] as const satisfies ReadonlyArray<{
  bucket: AdminOrdersDeadlineBucket;
  labelKey: string;
  label: string;
  color: string;
}>;

export function AdminOrdersDeadlineWidget({
  counts,
  activeBucket,
  onSelect,
}: AdminOrdersDeadlineWidgetProps) {
  const { t, i18n } = useTranslation();
  const total = DEADLINE_BUCKETS.reduce(
    (sum, item) => sum + counts[item.bucket],
    0,
  );
  const formatter = new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language);

  return (
    <section
      aria-labelledby="admin-orders-deadline-title"
      className="min-w-0 p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="admin-orders-deadline-title"
          className="text-xs font-medium text-foreground"
        >
          {t(
            "marketplace.ordersDashboard.overview.deadlines.title",
            "Deadline health",
          )}
        </h2>
        <span className="text-caption tabular-nums text-muted-foreground">
          {t("marketplace.ordersDashboard.overview.orderCount", {
            count: total,
            defaultValue_one: "{{count}} order",
            defaultValue_other: "{{count}} orders",
          })}
        </span>
      </div>

      <div
        className="mt-3 flex h-2.5 overflow-hidden rounded-sm bg-muted"
        aria-hidden
      >
        {DEADLINE_BUCKETS.filter((item) => counts[item.bucket] > 0).map(
          (item) => (
            <span
              key={item.bucket}
              style={{
                backgroundColor: item.color,
                width: `${(counts[item.bucket] / total) * 100}%`,
              }}
            />
          ),
        )}
      </div>

      <ul className="mt-2 grid grid-cols-2 gap-x-2">
        {DEADLINE_BUCKETS.map((item) => {
          const isActive = activeBucket === item.bucket;
          return (
            <li key={item.bucket}>
              <button
                type="button"
                aria-pressed={isActive}
                onClick={() => onSelect(item.bucket)}
                className={cn(
                  "flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-sm px-1.5 text-left text-caption outline-none transition-colors duration-150",
                  "hover:bg-foreground/5 focus-visible:ring-1 focus-visible:ring-ring motion-reduce:transition-none",
                  isActive &&
                    "bg-primary/5 text-primary dark:bg-primary/15",
                )}
              >
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {t(item.labelKey, item.label)}
                </span>
                <span className="tabular-nums text-foreground">
                  {formatter.format(counts[item.bucket])}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
